// src/App.js
import React, { useEffect, useState } from "react";
import "./styles/components.css";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { apiFetchJson } from "./utils/http";
import apiRoutes from "./api/apiRoutes";
import { getAccessToken, logout } from "./utils/auth";
import { startSessionWatch, stopSessionWatch } from "./utils/sessionManager";

import Dashboard from "./views/Dashboard";
import LoginPage from "./views/login/LoginPage";
import DeniedPage from "./views/login/DeniedPage";
import TerminosCondiciones from "./views/login/TerminosCondicionesPassword";

import DatosClientes from "./views/Clientes/DatosClientes/DatosClientes";
import VistaClientes from "./views/Clientes/DatosClientes/VerClientes";
import EditarCliente from "./views/Clientes/DatosClientes/EditarCliente";
import CambiarEstadoTarjetas from "./views/Clientes/DatosClientes/CambiarEstadoTarjetas";

import OverlayInactividad from "./components/OverlayInactividad";
import ModalHost from "./views/ModalHost";
import OverlayHost from "./components/OverlayHost";

const LogWrapper = ({ name, children }) => {
  console.log(`Renderizando ${name}`);
  return children;
};

function TerminosCondicionesRoute() {
  const navigate = useNavigate();
  const claims = JSON.parse(localStorage.getItem("claims") || "{}");
  const nombreUsuario = claims.sub || "";
  const userId = localStorage.getItem("userId");

  return (
    <TerminosCondiciones
      nombreUsuario={nombreUsuario}
      terminosUrl="/terminos-condiciones-flota.pdf"
      onVolver={() => navigate("/login")}
      onGuardado={async ({ password }) => {
        if (!userId) {
          throw new Error("No se pudo identificar el usuario logueado.");
        }

        await apiFetchJson(apiRoutes.usuarios.updateClave(userId), {
          method: "PUT",
          headers: {
            "Content-Type": "text/plain;charset=UTF-8",
          },
          body: password,
        });

        const newClaims = { ...(claims || {}), terminosCondiciones: "SI" };
        localStorage.setItem("claims", JSON.stringify(newClaims));
        localStorage.setItem("terminosCondiciones", "SI");
      }}
    />
  );
}

function App() {
  const [mostrarAvisoInactividad, setMostrarAvisoInactividad] = useState(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        window.dispatchEvent(new CustomEvent("app:esc"));
        console.log('[ESC] disparado -> CustomEvent("app:esc")');
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    const onForbidden = () => {
      window.dispatchEvent(
        new CustomEvent("notify", {
          detail: {
            type: "error",
            title: "Acceso denegado",
            message: "No tenés permisos para acceder a esta funcionalidad.",
          },
        })
      );
    };

    window.addEventListener("auth:forbidden", onForbidden);
    return () => window.removeEventListener("auth:forbidden", onForbidden);
  }, []);

  useEffect(() => {
    const onStorageChange = (e) => {
      if (e.key === "access_token" && e.newValue === null) {
        console.warn("[App] Token eliminado en otra pestaña. Cerrando sesión local.");
        stopSessionWatch();
        setMostrarAvisoInactividad(false);

        if ((window.location.hash || "#/") !== "#/") {
          window.location.hash = "#/";
        }
      }
    };

    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  useEffect(() => {
    const sessionOptions = {
      onInactivityWarning: setMostrarAvisoInactividad,
    };

    if (getAccessToken()) {
      startSessionWatch("App.js:boot", sessionOptions);
    }

    const onLogin = () => {
      setMostrarAvisoInactividad(false);
      startSessionWatch("App.js:auth:login", sessionOptions);
    };

    const onLogout = () => {
      setMostrarAvisoInactividad(false);
      stopSessionWatch();
    };

    window.addEventListener("auth:login", onLogin);
    window.addEventListener("auth:logout", onLogout);

    return () => {
      window.removeEventListener("auth:login", onLogin);
      window.removeEventListener("auth:logout", onLogout);
    };
  }, []);

  useEffect(() => {
    const openModal = (detail) => {
      if (window.__modal?.open) window.__modal.open(detail || {});
      else window.dispatchEvent(new CustomEvent("modal:open", { detail }));
    };

    const api = {
      success: (message, extra = {}) =>
        openModal({
          type: "success",
          title: "Operación realizada",
          message: String(message ?? ""),
          ...extra,
        }),
      error: (message, extra = {}) =>
        openModal({
          type: "error",
          title: "Error",
          message: String(message ?? ""),
          ...extra,
        }),
      info: (message, extra = {}) =>
        openModal({
          type: "info",
          title: "Información",
          message: String(message ?? ""),
          ...extra,
        }),
      warning: (message, extra = {}) =>
        openModal({
          type: "error",
          title: "Aviso",
          message: String(message ?? ""),
          ...extra,
        }),
    };

    window.__notify = api;

    const onNotify = (ev) => {
      const d = ev.detail || {};
      const t = (d.type || "info").toLowerCase();
      const fn = api[t] || api.info;
      fn(d.message, d);
    };

    window.addEventListener("notify", onNotify);

    return () => {
      if (window.__notify === api) window.__notify = undefined;
      window.removeEventListener("notify", onNotify);
    };
  }, []);

  return (
    <>
      <ModalHost />
      <OverlayHost />
      <OverlayInactividad visible={mostrarAvisoInactividad} />

      <Routes>
        <Route
          path="/"
          element={
            <LogWrapper name="LoginPage">
              <LoginPage />
            </LogWrapper>
          }
        />

        <Route
          path="/login"
          element={
            <LogWrapper name="LoginPage">
              <LoginPage />
            </LogWrapper>
          }
        />

        <Route
          path="/login/TerminosCondicionesPassword"
          element={
            <LogWrapper name="TerminosCondiciones">
              <TerminosCondicionesRoute />
            </LogWrapper>
          }
        />

        <Route
          path="/access-denied"
          element={
            <LogWrapper name="DeniedPage">
              <DeniedPage />
            </LogWrapper>
          }
        />

        <Route path="/dashboard" element={<Dashboard />}>
          <Route path="clientes" element={<DatosClientes />} />
          <Route path="clientes/:idCliente" element={<VistaClientes />} />
          <Route path="clientes/editar" element={<EditarCliente />} />
          <Route
            path="clientes/cambiar-estado"
            element={<CambiarEstadoTarjetas />}
          />
        </Route>

        <Route
          path="/tarjetas/configuracion/permisoTarjeta"
          element={<Navigate to="/dashboard/tarjetas/permisos" replace />}
        />

        <Route path="*" element={<div>Página no encontrada</div>} />
      </Routes>
    </>
  );
}

export default App;